import { Switch } from '@headlessui/react';

import type { CssSpacing } from '@/lib/css-types';
import { cn } from '@/lib/utils';

interface LabeledSwitchProps {
	checkedLabel: string;
	uncheckedLabel: string;
	checked: boolean;
	onChange: (value: boolean) => void;

	disabled?: boolean;
	/**
	 * Padding for the switch container.
	 * You can also customize padding via CSS variables:
	 * - `--p`: Override padding (e.g., `className="[--p:0.5rem]"`)
	 * - `--p-default`: Override default padding (e.g., `className="[--p-default:0.3rem]"`)
	 *
	 * @example
	 * ```tsx
	 * // Via prop
	 * <LabeledSwitch padding="0.5rem" />
	 *
	 * // Via CSS variable
	 * <LabeledSwitch className="[--p:0.1rem] sm:[--p:0.2rem]" />
	 * ```
	 */
	padding?: CssSpacing;
	className?: string;

	// Pill (sliding element) styling
	pillClassName?: string;

	// Label styling
	checkedLabelClassName?: string;
	uncheckedLabelClassName?: string;
}

function LabeledSwitch({
	checkedLabel,
	uncheckedLabel,
	checked,
	onChange,
	disabled,
	className,
	pillClassName,
	checkedLabelClassName,
	uncheckedLabelClassName,
	padding,
}: LabeledSwitchProps) {
	return (
		<Switch
			style={
				{
					'--p-default': '0.25rem',
					...(padding && { '--p': padding }),
				} as React.CSSProperties
			}
			disabled={disabled}
			checked={checked}
			onChange={onChange}
			className={cn(
				'group relative inline-grid cursor-pointer grid-cols-[1fr_1fr] rounded-full bg-gray-200 p-[var(--p,var(--p-default))] transition-[background-color,transform] duration-100 data-disabled:cursor-not-allowed data-disabled:opacity-50',
				className,
			)}
		>
			<span
				className={cn(
					'z-1 truncate px-2 py-0.5 text-gray-900 group-data-checked:text-gray-600 sm:px-3 sm:py-1',
					uncheckedLabelClassName,
				)}
			>
				{uncheckedLabel}
			</span>
			<span
				className={cn(
					'z-1 truncate px-2 py-0.5 text-gray-600 group-data-checked:text-gray-900 sm:px-3 sm:py-1',
					checkedLabelClassName,
				)}
			>
				{checkedLabel}
			</span>
			<span
				className={cn(
					'absolute inset-y-[var(--p,var(--p-default))] left-[var(--p,var(--p-default))] w-[calc(50%-var(--p,var(--p-default)))] bg-white transition-transform [border-radius:inherit] [transition-duration:inherit]',
					pillClassName,
					'translate-x-0 group-data-checked:translate-x-full',
				)}
			/>
		</Switch>
	);
}
export { LabeledSwitch };
